import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, Bell, BellRing, ChevronDown, LogOut } from 'lucide-react';
import { IpeBrandLogo } from '@/components/ipe/IpeBrandLogo';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { useSheinWorkspaceAuthOptional } from '@/shein-platform/context/SheinWorkspaceAuthContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import SheinShopSwitchModal from '@/shein-platform/components/SheinShopSwitchModal';
import SheinShopAvatar from '@/shein-platform/components/SheinShopAvatar';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import {
  IPE_SHEIN_GATE_PATH,
  SHEIN_SHOP_SWITCH_TOAST_KEY,
  isSheinShopAuthorized,
} from '@/shein-platform/constants/sheinRoutes';
import { workspaceSubtitleForShopType } from '@/shein-platform/utils/sheinShopCapabilities';
import {
  getSheinNoticeAlertCounts,
  markSheinNoticeChannelSeen,
  openSheinNotice,
  setSheinNoticeAlertsSafe,
  subscribeSheinNoticeAlerts,
} from '@/shein-platform/state/sheinNoticeStore';
import { allowDocumentUnload } from '@/shein-platform/hooks/allowDocumentUnload';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import IpeLocaleSwitcher from '@/i18n/IpeLocaleSwitcher';
import '@/styles/shein-platform/shein-platform.css';
import '@/styles/shein-platform/shein-product-ops.css';

const TOPBAR_BG = `${process.env.PUBLIC_URL || ''}/shein-gate/topbar-bg.png?v=1`;

export default function SheinShopTopBar() {
  const { t } = useTranslation('workbench');
  const {
    shops,
    scopedShops,
    activeShop,
    activeShopId,
    isAllShopsMode,
    expectedShopType,
    switchShop,
    loading,
  } = useSheinShop();
  const workspaceAuth = useSheinWorkspaceAuthOptional();
  const [menuOpen, setMenuOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [exiting, setExiting] = useState(false);
  const alertCounts = useSyncExternalStore(
    subscribeSheinNoticeAlerts,
    getSheinNoticeAlertCounts,
    () => ({ platform: 0, system: 0 }),
  );
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // 顶栏角标：与抽屉共用 store；失败不写空列表，避免关抽屉/竞态看起来像被清空
  useEffect(() => {
    if (!activeShopId || isAllShopsScope(activeShopId)) {
      setSheinNoticeAlertsSafe([], { allowEmpty: true, shopId: null });
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getAlerts(activeShopId)
      .then((res) => {
        if (cancelled) return;
        setSheinNoticeAlertsSafe(res, { allowEmpty: true, shopId: activeShopId });
      })
      .catch(() => {
        /* 保留 store 旧值，绝不因失败刷空 */
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const authorized = isAllShopsMode ? true : isSheinShopAuthorized(activeShop);
  const authLabel = isAllShopsMode
    ? t('platformBar.authorizedCount', { count: scopedShops.length })
    : (authorized ? t('shop.authConnected') : t('shop.authDisconnected'));
  const shopName = isAllShopsMode
    ? t('platformBar.allShops')
    : (activeShop?.shopName || (shops.length ? t('platformBar.pickShop') : t('shop.empty')));

  const handleSelectShop = (shop) => {
    const nextId = shop?.id != null ? String(shop.id) : '';
    if (nextId && nextId === String(activeShopId)) {
      setSwitchOpen(false);
      setMenuOpen(false);
      return;
    }
    const result = switchShop(isAllShopsScope(nextId) ? PLATFORM_ALL_SHOPS_ID : nextId);
    if (!result.ok) {
      showToast(result.message, 'error');
      return;
    }
    if (result.unchanged) {
      setSwitchOpen(false);
      setMenuOpen(false);
      return;
    }
    const shopLabel = isAllShopsScope(nextId)
      ? t('platformBar.allShops')
      : (shop.shopName || t('platformBar.shopFallback', { id: shop.id }));
    try {
      sessionStorage.setItem(
        SHEIN_SHOP_SWITCH_TOAST_KEY,
        JSON.stringify({ message: t('platformBar.switchedTo', { name: shopLabel }), type: 'success' }),
      );
    } catch {
      /* ignore */
    }
    allowDocumentUnload();
    window.location.reload();
  };

  const handleExitWorkbench = async () => {
    setMenuOpen(false);
    const ok = await showSheinConfirm({
      title: t('platformBar.exitTitle'),
      message: t('platformBar.exitMessage', { platform: 'SHEIN' }),
      confirmText: t('platformBar.exitConfirm'),
      cancelText: t('shop.cancel'),
      tone: 'warning',
    });
    if (!ok) return;
    setExiting(true);
    try {
      if (workspaceAuth?.lock) {
        await workspaceAuth.lock();
      }
    } catch {
      /* still leave */
    } finally {
      allowDocumentUnload();
      window.location.href = IPE_SHEIN_GATE_PATH;
    }
  };

  const onOpenNotice = useCallback((channel) => {
    setMenuOpen(false);
    openSheinNotice(channel);
    const revertSeen = markSheinNoticeChannelSeen(channel);
    if (!activeShopId || isAllShopsScope(activeShopId)) return;
    sheinPlatformAPI.markAlertsRead({
      shopId: activeShopId,
      channel: channel === 'system' ? 'system' : 'platform',
    }).catch(() => {
      revertSeen();
    });
  }, [activeShopId]);

  return (
    <header
      className="shein-platform__topbar"
      style={{ backgroundImage: `url(${TOPBAR_BG})` }}
    >
      <div className="shein-platform__brand">
        <IpeBrandLogo size={28} className="shein-platform__brand-logo" />
        <div className="shein-platform__brand-copy">
          <span className="shein-platform__brand-title">IPE · SHEIN</span>
          <span className="shein-platform__brand-subtitle">
            {workspaceSubtitleForShopType(activeShop?.shopType || expectedShopType || workspaceAuth?.workspaceShopType)}
          </span>
        </div>
      </div>

      <div className="shein-platform__topbar-right" ref={rootRef}>
        <span className="ipe-locale-switcher-dock" data-ipe-locale-dock="">
          <IpeLocaleSwitcher />
        </span>
        <div className="shein-platform__notice-actions" aria-label={t('platformBar.notices')}>
          <button
            type="button"
            className={`shein-platform__notice-btn${alertCounts.platform > 0 ? ' has-dot' : ''}`}
            aria-label={t('platformBar.platformNotice')}
            title={t('platformBar.platformNotice')}
            onClick={() => onOpenNotice('platform')}
          >
            <BellRing size={18} strokeWidth={2} />
            {alertCounts.platform > 0 ? (
              <span className="shein-platform__notice-dot">
                {alertCounts.platform > 9 ? '9+' : alertCounts.platform}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className={`shein-platform__notice-btn${alertCounts.system > 0 ? ' has-dot' : ''}`}
            aria-label={t('platformBar.systemNotice')}
            title={t('platformBar.systemNotice')}
            onClick={() => onOpenNotice('system')}
          >
            <Bell size={18} strokeWidth={2} />
            {alertCounts.system > 0 ? (
              <span className="shein-platform__notice-dot">
                {alertCounts.system > 9 ? '9+' : alertCounts.system}
              </span>
            ) : null}
          </button>
        </div>

        <div
          className={`shein-platform__shop-switcher${menuOpen ? ' is-open' : ''}`}
          onMouseEnter={() => {
            if (!loading) setMenuOpen(true);
          }}
          onMouseLeave={() => setMenuOpen(false)}
        >
          <button
            type="button"
            className={`shein-platform__shop-trigger${menuOpen ? ' is-open' : ''}${authorized ? ' is-authorized' : ''}`}
            onClick={() => {
              // 触控设备无 hover：点击切换；桌面以悬停为主
              if (loading) return;
              setMenuOpen((v) => !v);
            }}
            disabled={loading}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <SheinShopAvatar
              className="shein-platform__shop-avatar"
              avatarUrl={activeShop?.avatarUrl}
              size={28}
              emptyIcon={!activeShop && !isAllShopsMode}
            />
            <span className="shein-platform__shop-meta">
              <span className="shein-platform__shop-name">{shopName}</span>
              {activeShop || isAllShopsMode ? (
                <span className={`shein-platform__shop-auth${authorized ? ' is-on' : ' is-off'}`}>
                  <i className="shein-platform__shop-auth-dot" aria-hidden="true" />
                  {authLabel}
                </span>
              ) : (
                <span className="shein-platform__shop-auth is-off">{t('platformBar.noShopSelected')}</span>
              )}
            </span>
            <ChevronDown size={14} className="shein-platform__shop-chevron" />
          </button>

          {menuOpen ? (
            <div
              className="shein-platform__shop-menu"
              role="menu"
              onMouseEnter={() => setMenuOpen(true)}
            >
              <div className="shein-platform__shop-menu-current">
                <SheinShopAvatar
                  className="shein-platform__shop-option-avatar"
                  avatarUrl={activeShop?.avatarUrl}
                  size={32}
                  emptyIcon={!activeShop && !isAllShopsMode}
                />
                <div className="shein-platform__shop-option-body">
                  <span className="shein-platform__shop-option-name">{shopName}</span>
                  <span className="shein-platform__shop-option-meta">
                    {isAllShopsMode
                      ? t('platformBar.sameTypeAuthorized', { count: scopedShops.length })
                      : (activeShop
                        ? t('platformBar.authShopCount', { auth: authLabel, count: shops.length })
                        : t('platformBar.shopCount', { count: shops.length }))}
                  </span>
                </div>
              </div>

              <div className="shein-platform__shop-menu-divider" />

              {shops.length === 0 ? (
                <div className="shein-platform__shop-menu-empty">
                  <p>{t('platformBar.noShopsYet', { platform: 'SHEIN' })}</p>
                  <Link
                    to={`${IPE_WORKBENCH_PATH}?activeNav=platformShops`}
                    className="shein-platform__link"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('platformBar.goManage')}
                  </Link>
                </div>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="shein-platform__shop-menu-action"
                  onClick={() => {
                    setMenuOpen(false);
                    setSwitchOpen(true);
                  }}
                >
                  <ArrowLeftRight size={16} />
                  <span>{t('platformBar.switchShop')}</span>
                </button>
              )}

              <button
                type="button"
                role="menuitem"
                className="shein-platform__shop-menu-action shein-platform__shop-menu-action--danger"
                disabled={exiting}
                onClick={handleExitWorkbench}
              >
                <LogOut size={16} />
                <span>{exiting ? t('platformBar.exiting') : t('platformBar.exitWorkbench')}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <SheinShopSwitchModal
        open={switchOpen}
        shops={shops}
        activeShopId={activeShopId}
        workspaceShopType={expectedShopType || workspaceAuth?.workspaceShopType}
        loading={loading}
        onSelect={handleSelectShop}
        onClose={() => setSwitchOpen(false)}
      />
    </header>
  );
}
